import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { useZihinKilavuzu } from '../../contexts/ZihinKilavuzuContext';

// ==========================================
// TOOLTIP ARROW
// ==========================================

const TooltipArrow = ({ position, color = '#917AFF' }) => {
  const arrowPositions = {
    top: 'bottom-[-8px] left-1/2 -translate-x-1/2 rotate-180',
    bottom: 'top-[-8px] left-1/2 -translate-x-1/2',
    left: 'right-[-8px] top-1/2 -translate-y-1/2 rotate-90',
    right: 'left-[-8px] top-1/2 -translate-y-1/2 -rotate-90',
    center: 'hidden'
  };
  
  return (
    <motion.div
      className={`absolute w-4 h-4 ${arrowPositions[position] || arrowPositions.bottom}`}
      animate={{ opacity: [0.6, 1, 0.6] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      <svg viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path 
          d="M8 0L16 8H0L8 0Z" 
          fill="rgba(12,12,24,0.9)"
          stroke={color}
          strokeWidth="0.5"
        />
      </svg>
    </motion.div>
  );
};

// ==========================================
// MAIN TOOLTIP COMPONENT
// ==========================================

export default function ZihinKilavuzuTooltip() {
  const { activeHint, dismissHint, isOnboarding, skipOnboarding } = useZihinKilavuzu();
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState('bottom');
  
  // Calculate position for center hints
  const centerPosition = useMemo(() => {
    if (activeHint?.position === 'center' && !activeHint?.targetElement) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }
    return null;
  }, [activeHint]);
  
  // Calculate tooltip position for element-targeted hints
  useEffect(() => {
    if (!activeHint) return;
    
    // Handle center position
    if (activeHint.position === 'center' && !activeHint.targetElement) {
      setActualPosition('center');
      return;
    }
    
    if (!activeHint.targetElement) return;
    
    const calculatePosition = () => {
      const targetRect = activeHint.targetElement.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      
      if (!tooltipEl) return;
      
      const tooltipRect = tooltipEl.getBoundingClientRect();
      const padding = 16;
      const preferredPosition = activeHint.position || 'bottom';
      
      let top, left;
      let finalPosition = preferredPosition;
      
      switch (preferredPosition) {
        case 'top':
          top = targetRect.top - tooltipRect.height - padding;
          left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
          if (top < padding) {
            top = targetRect.bottom + padding;
            finalPosition = 'bottom';
          }
          break;
        case 'bottom':
          top = targetRect.bottom + padding;
          left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
          if (top + tooltipRect.height > window.innerHeight - padding) {
            top = targetRect.top - tooltipRect.height - padding;
            finalPosition = 'top';
          }
          break;
        case 'left':
          top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
          left = targetRect.left - tooltipRect.width - padding;
          if (left < padding) {
            left = targetRect.right + padding;
            finalPosition = 'right';
          }
          break;
        case 'right':
          top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
          left = targetRect.right + padding;
          if (left + tooltipRect.width > window.innerWidth - padding) {
            left = targetRect.left - tooltipRect.width - padding;
            finalPosition = 'left';
          }
          break;
        default:
          top = targetRect.bottom + padding;
          left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
      }
      
      // Boundary checks
      left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));
      top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding));
      
      setPosition({ top, left });
      setActualPosition(finalPosition);
    };
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(calculatePosition, 50);
    return () => clearTimeout(timer);
  }, [activeHint]);
  
  if (!activeHint) return null;
  
  const handleDismiss = () => {
    dismissHint(activeHint.id);
  };
  
  const handleSkipAll = () => {
    skipOnboarding();
  };
  
  const finalPosition = centerPosition || position;
  
  return (
    <AnimatePresence>
      {activeHint && (
        <>
          {/* Backdrop for blocking interaction */}
          {activeHint.behavior?.blockInteraction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm"
              onClick={handleDismiss}
            />
          )}
          
          {/* Spotlight effect on target */}
          {activeHint.targetElement && !activeHint.behavior?.blockInteraction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9997] pointer-events-none"
              style={{
                background: `radial-gradient(circle 150px at ${
                  activeHint.targetElement.getBoundingClientRect().left + 
                  activeHint.targetElement.getBoundingClientRect().width / 2
                }px ${
                  activeHint.targetElement.getBoundingClientRect().top + 
                  activeHint.targetElement.getBoundingClientRect().height / 2
                }px, transparent 0%, rgba(0,0,0,0.5) 100%)`
              }}
            />
          )}
          
          {/* Tooltip */}
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed z-[9999] max-w-[420px] w-[90vw] md:w-auto"
            style={{
              top: finalPosition.top,
              left: finalPosition.left,
              transform: finalPosition.transform
            }}
          >
            <div 
              className="relative rounded-[20px] p-[18px] md:p-6"
              style={{
                background: 'rgba(12, 12, 24, 0.85)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 0 24px rgba(145, 122, 255, 0.45), 0 8px 32px rgba(0, 0, 0, 0.4)'
              }}
            >
              {/* Close button */}
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 p-1.5 rounded-full text-[#A0A4B3] hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* Icon */}
              <div className="flex items-start gap-4 mb-3">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #7C3AED20, #22D3EE20)' }}
                >
                  <Sparkles className="w-5 h-5 text-[#B19CFF]" />
                </motion.div>
                
                <div className="flex-1 pr-6">
                  {/* Title */}
                  <h4 
                    className="text-lg font-semibold mb-1"
                    style={{ color: '#E6E6F7' }}
                  >
                    {activeHint.copy?.title}
                  </h4>
                  
                  {/* Body */}
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ color: '#A0A4B3' }}
                  >
                    {activeHint.copy?.body}
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                {isOnboarding && (
                  <button
                    onClick={handleSkipAll}
                    className="text-xs text-[#A0A4B3] hover:text-white transition-colors"
                  >
                    Tümünü geç
                  </button>
                )}
                
                {!isOnboarding && <div />}
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDismiss}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition-all"
                  style={{ 
                    background: 'linear-gradient(135deg, #7C3AED, #4A3FFF)',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
                  }}
                >
                  {activeHint.copy?.cta || 'Anladım'}
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
              
              {/* Arrow */}
              {actualPosition !== 'center' && (
                <TooltipArrow position={actualPosition} />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
